(() => {
  if (customElements.get('hxy-stock-filter')) return;
  class HxyStockFilter extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-hxy-stock-fallback]');
      if (!this.button) {
        const native = this.querySelector('a.hxy-stock-toggle');
        native?.addEventListener('keydown', (event) => {
          if (event.code === 'Space') { event.preventDefault(); native.click(); }
        });
        return;
      }
      this.status = this.querySelector('[data-hxy-stock-status]');
      this.controller = new AbortController();
      this.button.addEventListener('click', () => this.toggle(), { signal: this.controller.signal });
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.querySelector('#ProductGridContainer')) {
          this.fetchController?.abort();
          this.cards = null;
          this.listTemplate = null;
          this.original = null;
          this.enabled = false;
          this.button.disabled = false;
          this.button.setAttribute('aria-checked', 'false');
          this.button.classList.remove('is-active');
          this.status.textContent = '';
        }
      }, { signal: this.controller.signal });
      if (new URL(location.href).searchParams.get('hxy_stock') === '1') this.toggle();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.fetchController?.abort();
    }
    root() {
      return document.getElementById('ProductGridContainer');
    }
    cleanURL() {
      const url = new URL(location.href);
      url.searchParams.delete('hxy_stock');
      url.searchParams.delete('hxy_stock_page');
      return url;
    }
    updateURL(page) {
      const url = this.cleanURL();
      if (this.enabled) {
        url.searchParams.set('hxy_stock', '1');
        if (page > 1) url.searchParams.set('hxy_stock_page', String(page));
      }
      history.replaceState(history.state, '', url);
    }
    async collect(root) {
      const pages = Math.max(1, Number(root.dataset.hxyPages) || 1);
      const current = Number(root.dataset.hxyPage) || 1;
      const documents = new Map([[current, root.cloneNode(true)]]);
      const queue = Array.from({ length: pages }, (_, i) => i + 1).filter(page => page !== current);
      this.fetchController = new AbortController();
      const base = this.cleanURL();
      base.hash = '';
      const worker = async () => {
        while (queue.length) {
          const page = queue.shift();
          const url = new URL(base);
          url.searchParams.set('section_id', root.dataset.hxySection);
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { credentials: 'same-origin', signal: this.fetchController.signal });
          if (!response.ok) throw new Error('Collection request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextRoot = doc.getElementById('ProductGridContainer');
          if (!nextRoot || Number(nextRoot.dataset.hxyPage) !== page) throw new Error('Collection page mismatch');
          documents.set(page, nextRoot);
        }
      };
      try {
        await Promise.all(Array.from({ length: Math.min(3, queue.length) }, worker));
      } catch (error) {
        this.fetchController.abort();
        throw error;
      }
      const cards = [];
      const seen = new Set();
      for (let page = 1; page <= pages; page++) {
        const source = documents.get(page);
        if (!source) throw new Error('Collection incomplete');
        if (!this.listTemplate) {
          const list = source.querySelector('ul#product-grid');
          if (list) this.listTemplate = list.cloneNode(false);
        }
        source.querySelectorAll('#product-grid > [data-hxy-available="true"]').forEach(card => {
          const id = card.dataset.hxyProductId;
          if (id && !seen.has(id)) {
            seen.add(id);
            cards.push(document.importNode(card, true));
          }
        });
      }
      return cards;
    }
    renderPage(requestedPage = 1) {
      const root = this.root();
      const pageSize = Math.max(1, Number(root.dataset.hxyPerPage) || 36);
      const totalPages = Math.max(1, Math.ceil(this.cards.length / pageSize));
      const page = Math.min(totalPages, Math.max(1, Number(requestedPage) || 1));
      const wrapper = document.createElement('div');
      wrapper.className = 'collection page-width';
      if (this.cards.length && this.listTemplate) {
        const list = this.listTemplate.cloneNode(false);
        this.cards.slice((page - 1) * pageSize, page * pageSize).forEach((card, index) => {
          const node = card.cloneNode(true);
          node.classList.remove('scroll-trigger', 'animate--slide-in');
          node.style.removeProperty('--animation-order');
          if (index < 2) node.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
          list.append(node);
        });
        wrapper.append(list);
      } else {
        const empty = document.createElement('p');
        empty.className = 'hxy-stock-empty';
        empty.textContent = this.dataset.empty;
        wrapper.append(empty);
      }
      if (totalPages > 1) {
        const pagination = document.createElement('nav');
        pagination.className = 'hxy-stock-pagination';
        const makeButton = (label, nextPage, disabled) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = label;
          button.disabled = disabled;
          button.addEventListener('click', () => {
            this.renderPage(nextPage);
            this.button.focus({ preventScroll: true });
            this.scrollIntoView({ block: 'start', behavior: 'auto' });
          });
          return button;
        };
        const counter = document.createElement('span');
        counter.textContent = this.dataset.page.replace('__CURRENT__', page).replace('__TOTAL__', totalPages);
        pagination.append(makeButton(this.dataset.previous, page - 1, page === 1), counter, makeButton(this.dataset.next, page + 1, page === totalPages));
        wrapper.append(pagination);
      }
      root.replaceChildren(wrapper);
      this.status.textContent = this.cards.length ? this.dataset.count.replace('__COUNT__', this.cards.length) : this.dataset.empty;
      this.updateURL(page);
    }
    async toggle() {
      const root = this.root();
      if (!root || this.button.disabled) return;
      if (this.enabled) {
        root.replaceChildren(...Array.from(this.original.childNodes, node => node.cloneNode(true)));
        this.enabled = false;
        this.button.setAttribute('aria-checked', 'false');
        this.button.classList.remove('is-active');
        this.status.textContent = this.dataset.all;
        this.updateURL();
        return;
      }
      if (!this.original) {
        this.original = root.cloneNode(true);
        this.listTemplate = root.querySelector('ul#product-grid')?.cloneNode(false);
      }
      const requestedPage = new URL(location.href).searchParams.get('hxy_stock_page') || 1;
      this.button.disabled = true;
      root.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      try {
        if (!this.cards) this.cards = await this.collect(root);
        if (!this.isConnected || this.root() !== root) return;
        this.enabled = true;
        this.button.setAttribute('aria-checked', 'true');
        this.button.classList.add('is-active');
        this.renderPage(requestedPage);
      } catch (error) {
        if (this.isConnected) this.status.textContent = this.dataset.error;
      } finally {
        root.removeAttribute('aria-busy');
        this.button.disabled = false;
      }
    }
  }
  customElements.define('hxy-stock-filter', HxyStockFilter);
})();
